import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import './GradientMask.css';

const lerp = (a, b, n) => (1 - n) * a + n * b;
const getMousePos = e => ({ x: e.clientX, y: e.clientY });
const getRandomString = length => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
};

const colorways = {
    purple: 'linear-gradient(135deg, #fff 0%, rgb(102, 51, 238) 35%, rgb(142, 100, 255) 65%, rgb(249, 38, 114) 100%)',
    blue: 'linear-gradient(135deg, #fff 0%, rgb(20, 50, 220) 35%, rgb(50, 100, 255) 65%, rgb(80, 180, 255) 100%)',
    green: 'linear-gradient(135deg, #fff 0%, rgb(20, 189, 110) 35%, rgb(60, 220, 150) 65%, rgb(0, 230, 180) 100%)',
    red: 'linear-gradient(135deg, #fff 0%, rgb(255, 88, 90) 35%, rgb(255, 140, 120) 65%, rgb(255, 50, 50) 100%)',
    orange: 'linear-gradient(135deg, #fff 0%, rgb(255, 140, 40) 35%, rgb(255, 180, 80) 65%, rgb(255, 100, 20) 100%)',
    teal: 'linear-gradient(135deg, #fff 0%, rgb(0, 140, 120) 35%, rgb(0, 190, 160) 65%, rgb(50, 230, 170) 100%)',
    pink: 'linear-gradient(135deg, #fff 0%, rgb(220, 40, 120) 35%, rgb(255, 100, 160) 65%, rgb(255, 50, 200) 100%)',
};

const GradientMaskHover = ({ hoverRef, colorway = 'purple' }) => {
    const itemRef = useRef(null);
    const decoRef = useRef(null);
    const requestId = useRef(null);
    const mousepos = useRef({ x: 0, y: 0 });

    const renderedStyles = {
        x: { previous: 0, current: 0, amt: 0.1 },
        y: { previous: 0, current: 0, amt: 0.1 }
    };

    useEffect(() => {
        const itemElement = itemRef.current;
        const decoElement = decoRef.current;

        const handleMouseMove = (ev) => {
            mousepos.current = getMousePos(ev);
        };
        window.addEventListener('mousemove', handleMouseMove);

        const render = (isFirstTick) => {
            // Get the element's bounding box on every frame
            const rect = itemElement.getBoundingClientRect();

            // Calculate the mouse position relative to the element's top-left corner
            renderedStyles.x.current = mousepos.current.x - rect.left;
            renderedStyles.y.current = mousepos.current.y - rect.top;

            // On the first frame, set the previous values to the current ones for a smooth start
            if (isFirstTick) {
                renderedStyles.x.previous = renderedStyles.x.current;
                renderedStyles.y.previous = renderedStyles.y.current;
            }

            // Use linear interpolation for a smooth animation
            for (const key in renderedStyles) {
                renderedStyles[key].previous = lerp(renderedStyles[key].previous, renderedStyles[key].current, renderedStyles[key].amt);
            }

            // Apply the smoothed values to the CSS variables
            gsap.set(itemElement, {
                '--x': renderedStyles.x.previous,
                '--y': renderedStyles.y.previous
            });

            // Update the background text
            decoElement.innerHTML = getRandomString(5500);

            // Continue the animation loop
            requestId.current = requestAnimationFrame(() => render(false));
        };

        const handleMouseEnter = () => {
            gsap.to(decoElement, {
                duration: 0.5,
                ease: 'power3',
                opacity: 1
            });
            // Start the animation loop
            requestId.current = requestAnimationFrame(() => render(true));
        };

        const handleMouseLeave = () => {
            // Stop the animation loop
            if (requestId.current) {
                cancelAnimationFrame(requestId.current);
                requestId.current = null;
            }
            gsap.to(decoElement, {
                duration: 0.5,
                ease: 'power3',
                opacity: 0
            });
        };

        const target = hoverRef?.current || itemElement;
        target.addEventListener('mouseenter', handleMouseEnter);
        target.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            target.removeEventListener('mouseenter', handleMouseEnter);
            target.removeEventListener('mouseleave', handleMouseLeave);
            if (requestId.current) {
                cancelAnimationFrame(requestId.current);
            }
        };
    }, []);

    const gradient = colorways[colorway] || colorways.purple;

    return (
        <div className="gmh-item">
            <div ref={itemRef} className="gmh-item-img">
                <div className="gmh-item-img-content" >
                </div>
                <div ref={decoRef} className="gmh-item-img-deco" style={{ '--ascii-gradient': gradient } as React.CSSProperties}> </div>
            </div>
        </div>
    );
};

export default GradientMaskHover;

